import { Breadcrumbs, Container, Link, Skeleton, Stack } from '@mui/material'
import { ReactElement, createContext, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, Link as RRLink, generatePath, useLocation, useParams } from 'react-router-dom'
import { useInventory } from '../../../features/Inventories/queries/useInventory'
import { SCOPE_THREE_ROUTES } from '../../../navigation/types/scopeThreeRoutes'

export const InventoriesLayout = () => {
  const { t } = useTranslation('impact/InventoriesLayout')
  const [actionButton, setActionButton] = useState<ReactElement>()
  const [tabs, setTabs] = useState<ReactElement>()
  const { pathname } = useLocation()
  const { inventoryId } = useParams()
  const { data: inventory, isLoading } = useInventory(inventoryId)

  return (
    <LayoutContext.Provider value={{ setActionButton, setTabs }}>
      <Container style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Stack justifyContent="space-between" direction="row" alignItems="center" pr={3}>
          <Breadcrumbs
            sx={{
              p: 3,
              color: 'text.secondary',
              '.MuiBreadcrumbs-li:last-child': { color: 'text.primary' },
            }}
          >
            <Link component={RRLink} to={SCOPE_THREE_ROUTES.ROOT} underline="hover" color="inherit">
              {t('inventories')}
            </Link>
            {inventoryId ? (
              isLoading ? (
                <Skeleton width="140px" />
              ) : (
                <Link
                  component={RRLink}
                  underline="hover"
                  to={generatePath(SCOPE_THREE_ROUTES.DETAIL, {
                    inventoryId,
                  })}
                  aria-current="page"
                  color="inherit"
                >
                  {inventory?.item.name}
                </Link>
              )
            ) : null}
            {pathname === SCOPE_THREE_ROUTES.CREATE && (
              <Link underline="none" color="inherit">
                {t('newInventory')}
              </Link>
            )}
            {!!inventoryId && pathname === generatePath(SCOPE_THREE_ROUTES.SETUP, { inventoryId }) && (
              <Link underline="none" color="inherit">
                {t('setup')}
              </Link>
            )}
            {!!inventoryId &&
              pathname === generatePath(SCOPE_THREE_ROUTES.TARGETS, { inventoryId }) && (
                <Link underline="none" color="inherit">
                  {t('setTargets')}
                </Link>
              )}
          </Breadcrumbs>
          <Stack direction="row" spacing={2}>
            {tabs}
            {actionButton}
          </Stack>
        </Stack>
      </Container>
      <Outlet />
    </LayoutContext.Provider>
  )
}

export type LayoutContextValue = {
  setActionButton: (element?: ReactElement) => void
  setTabs: (element?: ReactElement) => void
}

export const LayoutContext = createContext<LayoutContextValue>({
  setActionButton: () => console.log('setActionButton not available'),
  setTabs: () => console.log('setTabs not available'),
})

export const useLayoutContext = () => useContext(LayoutContext)
